/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import { useRovoEntitlementResource } from '@jira/router-resources__rovo-entitlement/src/useRovoEntitlementResource.tsx';
import { NAVIGATION_ITEM_ID } from '@jira/navigation-apps__atlassian-navigation-ui/src/constants.tsx';
import { TopLevelErrorBoundary } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/error-boundary/index.tsx';
import { Box } from '@atlaskit/primitives/compiled/box';
import { ChatContextSubscriber } from '@atlassian/conversation-assistant-store/controllers/chat-context';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant/types';
import { ChatOpenerSubscriber } from '@atlassian/conversation-assistant/controllers/chat-opener-subscriber';
import { RovoEntitlementSetter } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { ConversationAssistantButtonNav4 as AssistantButtonNav4 } from '@atlassian/conversation-assistant-ui-components/ui/ConversationAssistantButton';
import { useTopMenus } from '@jira/navigation-apps__common/src/controllers/top-menus/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useOrgId } from '@atlassian/jira-tenant-context-controller/src/components/org-id/index.tsx';
import { useConversationAssistantSidebarApi } from '@jira/platform__conversation-assistant/src/useConversationAssistantSidebarApi.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { fg } from '@atlassian/jira-feature-gating';
import { usePanelManager } from '@atlassian/panel-system';

const AssistantButton = AssistantButtonNav4;

const ConversationAssistantButton = () => {
	const [_topMenusValue, { off: closeAllMenus }] = useTopMenus();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const orgId = useOrgId();
	const {
		isMiniModalAvailable,
		toggleConversationAssistantUIMode,
		openConversationAssistantUIMode,
		mode,
	} = useRovoExperienceAPIStore();
	const sidebarApi = useConversationAssistantSidebarApi({ isMinimised: false, toTop: true });
	const { state: panelState, manager } = usePanelManager();

	const isActive = sidebarApi.isSidebarOpen();

	const onButtonClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'navigationItem',
			}),
			'aiMate',
			{ opened: true },
		);

		if (fg('ads-panel-system-jira-admin')) {
			if (panelState.activePanels[0]) {
				const currentPanelInstanceId = panelState.activePanels[0].instanceId;
				manager.closePanel(currentPanelInstanceId);
			}
		}

		toggleConversationAssistantUIMode({
			targetMode: ConversationAssistantUIModes.SIDEBAR,
			sidebarApi,
		});

		closeAllMenus();
	}, [
		closeAllMenus,
		createAnalyticsEvent,
		toggleConversationAssistantUIMode,
		sidebarApi,
		panelState.activePanels,
		manager,
	]);

	const buttonRender = (
		<Box
			as="span"
			testId="atlassian-navigation.ui.conversation-assistant.app-navigation-ai-mate"
			role="listitem"
		>
			<AssistantButton
				onClick={onButtonClick}
				isActive={isActive}
				orgId={orgId}
				siteId={cloudId}
				productKey="jira"
				enableOnboardingSpotlight
			/>
		</Box>
	);

	return (
		<>
			{buttonRender}
			<ChatContextSubscriber />
			<ChatOpenerSubscriber
				onTriggerOpeningChat={(_conversationId, openChatMode, source) => {
					if (
						(source === 'input-prompt-box' && expVal('jira_free_gen_editor', 'isEnabled', false)) ||
						(source === 'ai-mate' && fg('add_source_for_mini_rovo_trigger'))
					) {
						if (isMiniModalAvailable) {
							openConversationAssistantUIMode({
								targetMode: ConversationAssistantUIModes.MINI_MODAL,
								sidebarApi,
							});
						} else {
							openConversationAssistantUIMode({
								targetMode: ConversationAssistantUIModes.SIDEBAR,
								sidebarApi,
							});
						}
						return;
					}
					const resolvedMode = openChatMode ?? mode;
					openConversationAssistantUIMode({
						targetMode:
							resolvedMode === 'mini-modal' && isMiniModalAvailable
								? ConversationAssistantUIModes.MINI_MODAL
								: ConversationAssistantUIModes.SIDEBAR,
						sidebarApi,
					});
				}}
			/>
		</>
	);
};

export const ConversationAssistant = (): React.JSX.Element => {
	const { data } = useRovoEntitlementResource();

	const shouldShowConversationAssistantButton = data?.isRovoLLMEnabled ?? false;

	const buttonRender = shouldShowConversationAssistantButton ? (
		<ConversationAssistantButton />
	) : null;

	return (
		<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.CONVERSATION_ASSISTANT}>
			{buttonRender}
			<RovoEntitlementSetter isRovoEnabled={shouldShowConversationAssistantButton} />
		</TopLevelErrorBoundary>
	);
};
